import {
  SyntheticEvent, useEffect,
} from 'react';

// Third-party
import Box from '@mui/material/Box';
import TabPanel from '@mui/lab/TabPanel';
import { useTranslation } from 'react-i18next';
import TabContext from '@mui/lab/TabContext';
import AppBar from '@mui/material/AppBar';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { useTheme } from '@mui/material';

// Components
import Divider from '@mui/material/Divider';
import Card from '@mui/material/Card';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { AllSubmissionsNav } from './AllSubmissionsNav';
import { DailySubmissionsNav } from './DailySubmissionsNav';
import { ToSubmitNav } from './ToSubmitNav';

// Hooks
import { useAppDispatch, useAppSelector } from '../../../../hooks/useAppStore';

// Redux-toolkit
import { tasklistsActions } from '../../../../store/tasklists/slice';
import { getSubmissionsTab } from '../../../../store/tasklists/selectors';

// Types
import { TEmptySubmission, TSubmission, TTasklist } from '../../../../types';
import { BaseFilterMenu } from '../../../../components/base';
import { useFilterSubmissions } from '../hooks';
import { useFilter } from '../../../../hooks/useFilter';

const SubmissionsTabs = () => {
  const { t } = useTranslation('tasklists');
  const theme = useTheme() as TCustomTheme;
  const { isLight } = useCustomTheme(theme);
  const dispatch = useAppDispatch();
  const tab = useAppSelector(getSubmissionsTab);
  const { filterSubmissionItems, getSubmissionsByFilters } = useFilterSubmissions();
  const { filterItems } = useFilter(filterSubmissionItems, false, true);

  useEffect(() => {
    dispatch(tasklistsActions.setSelectedDailySubmission({} as TSubmission));
    dispatch(tasklistsActions.setSelectedSubmission({} as TEmptySubmission));
  }, []);

  const tabs = [
    {
      label: t('allSubmissions'),
      value: '1',
    },
    {
      label: t('todaySubmissions'),
      value: '2',
    },
    {
      label: t('toSubmit'),
      value: '3',
    },
  ];

  const handleTabChange = (_event: SyntheticEvent, newValue: string) => {
    switch (newValue) {
      case '1':
        dispatch(tasklistsActions.setSelectedTasklistToSubmit({} as TTasklist));
        dispatch(tasklistsActions.setSelectedDailySubmission({} as TSubmission));
        break;
      case '2':
        dispatch(tasklistsActions.setSelectedTasklistToSubmit({} as TTasklist));
        dispatch(tasklistsActions.setSelectedSubmission({} as TEmptySubmission));
        break;
      case '3':
        dispatch(tasklistsActions.setSelectedDailySubmission({} as TSubmission));
        dispatch(tasklistsActions.setSelectedSubmission({} as TEmptySubmission));
        break;
      default:
    }

    dispatch(tasklistsActions.setSubmissionsTab(newValue));
  };

  return (
    <Box
      component={Card}
      sx={{
        bgcolor: 'background.paper',
        width: 'auto',
        height: '76vh',
        overflow: 'auto',
        position: 'relative',
      }}
    >
      <TabContext
        value={tab}
      >
        <AppBar
          sx={{
            borderRadius: 1,
            bgcolor: isLight ? 'white' : theme.palette.grey[800],
          }}
          position="static"
        >
          <Tabs
            value={tab}
            onChange={handleTabChange}
            variant="fullWidth"
          >
            {tabs.map((tab) => (
              <Tab key={tab.value} label={tab.label} value={tab.value} />
            ))}
          </Tabs>
          {tab === '1' && (
          <>
            <Divider sx={{ borderStyle: 'dashed' }} />
            <Box py={0.4} mr={2} display="flex" justifyContent="flex-end">
              <BaseFilterMenu items={filterItems} />
            </Box>
          </>
          )}
        </AppBar>
        <TabPanel
          sx={{
            px: 0,
            pt: 0,
          }}
          value="1"
        >
          <AllSubmissionsNav submissionsByFilters={getSubmissionsByFilters} />
        </TabPanel>
        <TabPanel
          sx={{
            px: 0,
            pt: 0,
          }}
          value="2"
        >
          <DailySubmissionsNav />
        </TabPanel>
        <TabPanel
          sx={{
            px: 0,
            pt: 0,
          }}
          value="3"
        >
          <ToSubmitNav />
        </TabPanel>
      </TabContext>
    </Box>
  );
};

export {
  SubmissionsTabs,
};
